import { useEffect, useMemo } from 'react';
import { useEditor } from '../context/EditorContext';
import { offPageFlows } from '../lib/storyFlow';
import { documentObjects, isLinkedText } from '../lib/storyChain';
import { useFontsVersion } from './useFontsVersion';

/**
 * Keeps `state.textFlow` complete for stories that span pages. The canvas measures the boxes it renders (current page +
 * scratch area); this hook measures every other linked box off-screen so a continuation on page 3 shows the right words
 * even when page 1 (where the story lives) is not on screen — and so exports / thumbnails of other pages are right.
 * Runs only when something that changes a flow changed (a linked box's size, text or formatting; the rendered flows;
 * the loaded fonts) — never for a plain move — and waits until a drag / resize is over.
 */
export const useStoryFlow = (renderedIds) => {
  const { state, rawDispatch } = useEditor();
  const fontsVersion = useFontsVersion();
  const { pages, scratch, textFlow, viewMode, interacting } = state;
  // what the measurement depends on: every linked box's geometry (not position) and properties, in document order
  const signature = useMemo(() => documentObjects(pages, scratch).filter(isLinkedText).map((o) => `${o.id}|${Math.round(o.width)}|${Math.round(o.height)}|${JSON.stringify(o.properties || {})}`).join('\n'), [pages, scratch]);
  useEffect(() => {
    if (viewMode === 'master' || interacting || !signature) return undefined;
    const t = window.setTimeout(() => {
      const updates = offPageFlows(pages, scratch, textFlow, renderedIds);
      if (Object.keys(updates).length) rawDispatch({ type: 'SET_TEXT_FLOWS', payload: updates });
    }, 30);
    return () => window.clearTimeout(t);
    // pages / scratch are read for the measurement but only `signature` decides when to re-measure
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature, textFlow, renderedIds, viewMode, interacting, fontsVersion, rawDispatch]);
};
